import Axios, { AxiosError } from "axios";
import { getAuthHeaders } from "../common/helpers";

export interface LanguageDto {
  id: number;
  languageName: string;
  isoCode: string;
  enabled: boolean;
}

export interface DialectDto {
  id: number;
  languageId: number;
  languageName: string;
  languageIsoCode: string;
  dialectName: string;
  enabled: boolean;
}

export interface DialectVoiceDto {
  id: number;
  voiceId: string;
  name: string;
  accent: string | null;
  gender: number;
  isDefault: boolean;
}

interface ApiEnvelope<T> {
  data?: T;
}

/**
 * Build the ace-common base URL from {@code REACT_APP_BASE_URL}.
 * REACT_APP_BASE_URL is configured per-environment as the avatar-core path
 * on the gateway (e.g. {@code https://avatar-api.abg-mynd.com/avatar-core/}).
 * ace-common lives on the same gateway under {@code /ace-common}, so we
 * strip the avatar-core suffix and append the ace-common one.
 */
function aceCommonBaseUrl(): string {
  const raw = process.env.REACT_APP_BASE_URL ?? "";
  // Drop trailing slash, then strip /avatar-core if present
  const trimmed = raw.replace(/\/+$/, "");
  const gatewayRoot = trimmed.replace(/\/avatar-core$/, "");
  return `${gatewayRoot}/ace-common`;
}

const isAuthRejection = (err: unknown): boolean => {
  const ax = err as AxiosError;
  const status = ax?.response?.status;
  return status === 401 || status === 403;
};

/**
 * Fetch the enabled languages from ace-common. Returns an empty array on
 * authentication rejection so guest sessions can fall back to English
 * without spamming the toast/error UI (mirrors the chatbot client's UX).
 */
export async function getEnabledLanguages(): Promise<LanguageDto[]> {
  const url = `${aceCommonBaseUrl()}/api/languages/enabled`;
  try {
    const res = await Axios.get<ApiEnvelope<LanguageDto[]>>(url, {
      headers: getAuthHeaders(),
    });
    const list = Array.isArray(res.data?.data) ? (res.data!.data as LanguageDto[]) : [];
    return list.filter((l) => !!l && l.enabled !== false);
  } catch (err) {
    if (isAuthRejection(err)) return [];
    throw err;
  }
}

/**
 * Fetch dialects for a given base ISO code (e.g. {@code "ar"} → Egyptian,
 * Lebanese, ...). Returns an empty array on auth rejection or 404.
 */
export async function getDialects(isoCode: string): Promise<DialectDto[]> {
  if (!isoCode) return [];
  const url = `${aceCommonBaseUrl()}/api/languages/${encodeURIComponent(isoCode)}/dialects`;
  try {
    const res = await Axios.get<ApiEnvelope<DialectDto[]>>(url, {
      headers: getAuthHeaders(),
    });
    const list = Array.isArray(res.data?.data) ? (res.data!.data as DialectDto[]) : [];
    return list.filter((d) => !!d && d.enabled !== false);
  } catch (err) {
    const ax = err as AxiosError;
    if (isAuthRejection(err) || ax?.response?.status === 404) return [];
    throw err;
  }
}

/**
 * Fetch voice IDs configured for a dialect (e.g. {@code "ar-SA"}). Talks to
 * avatar-core's {@code /api/dialects/{isoCode}/voices} endpoint. Returns an
 * empty array on 401/403/404 so the UI can fall back gracefully.
 */
export async function getVoicesForDialect(dialectIsoCode: string): Promise<DialectVoiceDto[]> {
  if (!dialectIsoCode) return [];
  const url = `${process.env.REACT_APP_BASE_URL ?? ""}api/dialects/${encodeURIComponent(dialectIsoCode)}/voices`;
  try {
    const res = await Axios.get<ApiEnvelope<DialectVoiceDto[]>>(url, {
      headers: getAuthHeaders(),
    });
    return Array.isArray(res.data?.data) ? (res.data!.data as DialectVoiceDto[]) : [];
  } catch (err) {
    const ax = err as AxiosError;
    if (isAuthRejection(err) || ax?.response?.status === 404) return [];
    throw err;
  }
}
